export type PulseAttribution = {
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_term?: string;
    utm_content?: string;
    referrer_host?: string;
};

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;
const STORAGE_KEY = '_cin_pulse_attribution';
const MAX_LEN = 200;

/** utm_* params plus the referrer host when it is a different site than `ownHost`. Pure. */
export function parseUtm(search: string, referrer: string, ownHost?: string): PulseAttribution {
    const params = new URLSearchParams(search);
    const out: PulseAttribution = {};
    for (const key of UTM_KEYS) {
        const value = params.get(key)?.trim().slice(0, MAX_LEN);
        if (value) out[key] = value;
    }
    try {
        const host = referrer ? new URL(referrer).hostname : '';
        if (host && host !== ownHost) out.referrer_host = host;
    } catch {
        // malformed referrer: no host
    }
    return out;
}

/**
 * First capture per session wins: later pages (internal navigation, UTM-less) must not overwrite the
 * landing attribution. Storage failures (private mode) fall back to the current page's values.
 */
export function loadOrCaptureAttribution(
    storage: Pick<Storage, 'getItem' | 'setItem'> | null | undefined,
    search: string,
    referrer: string,
    ownHost?: string
): PulseAttribution {
    try {
        const stored = storage?.getItem(STORAGE_KEY);
        if (stored) return JSON.parse(stored) as PulseAttribution;
    } catch {
        // unreadable: capture afresh
    }
    const captured = parseUtm(search, referrer, ownHost);
    try {
        storage?.setItem(STORAGE_KEY, JSON.stringify(captured));
    } catch {
        // storage unavailable: attribution lives for this page load only
    }
    return captured;
}
